import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  teamService,
  invitationsService,
  type InviteMemberDto,
  type UpdateMemberDto,
  type TeamRole,
} from '@/shared/services/team.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useAuthStore } from '@/shared/stores/auth.store';

const TEAM_KEY = 'team-members';
const MY_MEMBERSHIP_KEY = 'team-me';
const INVITES_KEY = 'team-invites';
const SEATS_KEY = 'team-seats';
export const MY_INVITES_KEY = ['me', 'invites'] as const;

/** The roles that read the team list (GET /team): the API refuses the others (AL-06). */
export const TEAM_READ_ROLES: ReadonlySet<TeamRole> = new Set<TeamRole>(['owner', 'admin', 'editor']);

/** My own role on the brand in view (GET /team/me), for every role. */
export function useMyMembership() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [MY_MEMBERSHIP_KEY, wsId],
    queryFn: () => teamService.myMembership(),
    enabled: !!wsId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The brand's members. Only asked for by the roles that may read them
 * (owner, admin, editor); a viewer, client or billing admin gets no list
 * rather than a 403. The owner is known from the brand row at once.
 */
export function useTeamMembers() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const ownerId = useWorkspaceStore((s) => s.currentWorkspace?.ownerId ?? null);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isOwner = !!userId && ownerId === userId;
  const { data: me } = useMyMembership();
  const mayRead = isOwner || (!!me && TEAM_READ_ROLES.has(me.role));
  return useQuery({
    queryKey: [TEAM_KEY, wsId],
    queryFn: () => teamService.listMembers(),
    enabled: !!wsId && mayRead,
    // Invalidated by invite/update/remove mutations.
    staleTime: 5 * 60 * 1000,
  });
}

/** Seats: members, pending invites and the plan's limit. */
export function useTeamSeats() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [SEATS_KEY, wsId],
    queryFn: () => teamService.seats(),
    enabled: !!wsId,
    staleTime: 60 * 1000,
  });
}

/** Invitations of the last 30 days that were not accepted. Managers only. */
export function useTeamInvites(enabled: boolean) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [INVITES_KEY, wsId],
    queryFn: () => teamService.listInvites(),
    enabled: !!wsId && enabled,
    staleTime: 60 * 1000,
  });
}

function useInvalidateTeam(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [TEAM_KEY] });
    void queryClient.invalidateQueries({ queryKey: [INVITES_KEY] });
    void queryClient.invalidateQueries({ queryKey: [SEATS_KEY] });
  };
}

export function useInviteMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (dto: InviteMemberDto) => teamService.inviteMember(dto),
    onSuccess: invalidate,
  });
}

export function useUpdateMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ memberId, dto }: { memberId: string; dto: UpdateMemberDto }) => teamService.updateMember(memberId, dto),
    onSuccess: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (memberId: string) => teamService.removeMember(memberId),
    onSuccess: invalidate,
  });
}

export function useResendInvite() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (inviteId: string) => teamService.resendInvite(inviteId),
    onSuccess: invalidate,
  });
}

export function useRevokeInvite() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (inviteId: string) => teamService.revokeInvite(inviteId),
    onSuccess: invalidate,
  });
}

/** Leave the brand in view; the brand list and everything team-shaped refresh. */
export function useLeaveBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => teamService.leave(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workspaces', 'me'] });
      void queryClient.invalidateQueries({ queryKey: [TEAM_KEY] });
    },
  });
}

/** My pending invitations across brands (the switcher's invites section). */
export function useMyInvites(enabled = true) {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: MY_INVITES_KEY,
    queryFn: () => invitationsService.mine(),
    enabled: signedIn && enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * After accepting, the new brand shows in the switcher at once (AL-59):
 * the brand list, the team lists and my invites are all stale.
 */
export function useInvalidateAfterAccept(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'me'] }),
      queryClient.invalidateQueries({ queryKey: MY_INVITES_KEY }),
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY] }),
      queryClient.invalidateQueries({ queryKey: [MY_MEMBERSHIP_KEY] }),
    ]);
  };
}
